/**
 * Where a lead came from: the CTA clicked, the page and the section. Recorded on every `[data-cta]`
 * click and kept for the session, so the /demo form can attribute the lead.
 */
import { findRoute } from '../../config/routes';
import { isRecord } from '../../lib/guards';
import { readStorageJson, STORAGE_KEYS, writeStorage } from '../../lib/storage';
import { trackEvent } from '../analytics/analytics';

export interface LeadContext {
  readonly cta_source: string;
  readonly page_source: string;
  readonly section_source: string;
}

let lastContext: LeadContext | null = null;

const pageSourceFor = (path: string | null): string =>
  (path ? (findRoute(path)?.pageName ?? path.replace(/^\//, '')) : '') || 'home';

function sectionSourceFor(link: HTMLElement): string {
  const section = link.closest('section[id],header,footer');
  if (section?.id) return section.id;
  if (link.closest('header')) return 'header';
  if (link.closest('footer')) return 'footer';
  return '';
}

export function recordCtaClick(link: HTMLElement, currentPath: string | null): void {
  lastContext = {
    cta_source: link.dataset.cta ?? '',
    page_source: pageSourceFor(currentPath),
    section_source: sectionSourceFor(link),
  };
  writeStorage('session', STORAGE_KEYS.leadContext, JSON.stringify(lastContext));
  trackEvent('cta_click', { ...lastContext });
}

function parseStoredContext(value: unknown): LeadContext | null {
  if (!isRecord(value)) return null;
  const { cta_source: cta, page_source: page, section_source: section } = value;
  return typeof cta === 'string' && typeof page === 'string'
    ? { cta_source: cta, page_source: page, section_source: typeof section === 'string' ? section : '' }
    : null;
}

export function getLeadContext(): LeadContext {
  return (
    lastContext ??
    parseStoredContext(readStorageJson('session', STORAGE_KEYS.leadContext)) ?? {
      cta_source: 'direct',
      page_source: document.referrer ? 'external' : 'direct',
      section_source: '',
    }
  );
}
